import React from 'react';
import { createRoot } from 'react-dom/client';
import { FeatureFlagUI } from './FeatureFlagUI.js';
import { LocalStorage } from './local-storage.js';
import { getUrlParameter } from './url.js';

/**
 * FeatureFlag class for managing feature flags.
 *
 * @example
 * Call before booting the app:
 * await FeatureFlag.init(`//api.test.bankmonitor.hu/api/public/config/feature/flags`);
 *
 * @example
 * Use:
 * if (FeatureFlag.isEnabled('feature-1')) {
 *   console.log('feature-1 is enabled');
 * }
 *
 * @example
 * Modify feature flag in frontend:
 * Enable a feature:
 * /{url}/?featureEnable=feature-2
 *
 * Disable a feature:
 * /{url}/?featureDisable=feature-2
 */ class FeatureFlag {
    static async init(featureFlagEndpoint) {
        if (this.flags === undefined) {
            try {
                this.flags = this.cleanFlagsPath(await fetch(featureFlagEndpoint).then((response)=>response.json()));
            } catch (error) {
                this.flags = {};
            }
            this.checkModifiedFlag();
            this.showFeatureFlagUi();
        }
    }
    static isEnabled(flag) {
        if (this.flags === undefined) {
            console.log("Not called the init yet!");
        }
        const enabledInConfig = this.enabledInConfig(flag);
        if (this.enabledModifiedFlag(flag)) {
            return !enabledInConfig;
        }
        return enabledInConfig;
    }
    static enabledInConfig(flag) {
        return this.flags?.[flag] === true;
    }
    static cleanFlagsPath(flags) {
        const cleanedFlags = {};
        Object.keys(flags).forEach((key)=>{
            cleanedFlags[key.replace("feature.flag.", "")] = flags[key];
        });
        return cleanedFlags;
    }
    static createDisableUrlForFeature() {
        const newUrl = new URL(location.href);
        newUrl.searchParams.delete("featureEnable");
        newUrl.searchParams.set("featureDisable", this.getLastModifiedFlag());
        return newUrl.toString();
    }
    static showFeatureFlagUi() {
        // create a minimal UI
        if (this.getModifiedFlags().length > 0) {
            const ui = document.createElement("div");
            ui.id = "feature-flag-ui";
            document.body.appendChild(ui);
            const root = createRoot(document.getElementById("feature-flag-ui"));
            root.render(/*#__PURE__*/ React.createElement(FeatureFlagUI, null));
        }
    }
    static checkModifiedFlag() {
        const enabledFeature = getUrlParameter("featureEnable");
        const disableFeature = getUrlParameter("featureDisable");
        let modifiedFlags = this.getModifiedFlags();
        if (enabledFeature) {
            const disbabledInConfig = !this.enabledInConfig(enabledFeature);
            const isModified = modifiedFlags.includes(enabledFeature);
            if (disbabledInConfig) {
                // ha disabled vagy nem létezik a configban
                if (!isModified) {
                    modifiedFlags = [
                        ...modifiedFlags,
                        enabledFeature
                    ];
                } else {
                    //ha ki lett kapcsolva kézzel
                    modifiedFlags = modifiedFlags.filter((flag)=>flag !== enabledFeature);
                }
            }
            this.modifiedFlags = modifiedFlags;
            LocalStorage.set("modifiedFlags", JSON.stringify(modifiedFlags));
        }
        if (disableFeature) {
            const enabledInConfig = this.enabledInConfig(disableFeature);
            const isModified = modifiedFlags.includes(disableFeature);
            if (isModified) {
                //ha már be lett kapcsolva kézzel
                modifiedFlags = modifiedFlags.filter((flag)=>flag !== disableFeature);
            } else if (enabledInConfig) {
                // ha enabled a configban
                modifiedFlags = [
                    ...modifiedFlags,
                    disableFeature
                ];
            }
            this.modifiedFlags = modifiedFlags;
            LocalStorage.set("modifiedFlags", JSON.stringify(modifiedFlags));
        }
        // reset modified flags
        this.modifiedFlags = undefined;
    }
    static enabledModifiedFlag(flag) {
        return this.getModifiedFlags().includes(flag);
    }
    static getModifiedFlags() {
        if (this.modifiedFlags === undefined) {
            this.modifiedFlags = JSON.parse(LocalStorage.get("modifiedFlags"));
        }
        return this.modifiedFlags || [];
    }
    static getLastModifiedFlag() {
        return this.getModifiedFlags().pop();
    }
    static reset() {
        this.flags = undefined;
        this.modifiedFlags = undefined;
    }
}

export { FeatureFlag };
//# sourceMappingURL=FeatureFlag.js.map
